import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  ImagePlus,
  Plus,
  Save,
  Trash2,
} from "lucide-react";

import { adminApi } from "@/lib/api";
import {
  AdminInput,
  AdminSelect,
  AdminTextarea,
  EmptyState,
  FieldLabel,
} from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const TABS = [
  "basic",
  "motivation",
  "summary",
  "details",
  "design_team",
  "before_after",
  "gallery",
  "videos",
  "seo",
];

function getDetailExtraRows(extra) {
  if (!extra) return [];

  if (Array.isArray(extra)) {
    return extra.map((item) => ({
      label: item?.label || item?.key || item?.name || "",
      value: item?.value || item?.text || "",
    }));
  }

  if (typeof extra === "object") {
    return Object.entries(extra).map(([label, value]) => ({
      label,
      value: value ?? "",
    }));
  }

  return [];
}

function detailRowsToObject(rows) {
  return (rows || []).reduce((acc, item) => {
    const label = String(item?.label || "").trim();
    const value = item?.value ?? "";

    if (!label) return acc;

    acc[label] = value;
    return acc;
  }, {});
}

function normalizeDetailsForSave(details = {}) {
  return {
    ...(details || {}),
    extra: detailRowsToObject(getDetailExtraRows(details?.extra)),
  };
}

export default function AdminProjectEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [p, setP] = useState(null);
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getProject(slug).then(setP).catch(() => setP(null));
  }, [slug]);

  const save = async () => {
    if (!p) return;

    setSaving(true);

    try {
      const patch = {
        title: p.title,
        short_label: p.short_label,
        cover_image: p.cover_image,
        motivation: p.motivation,
        summary: p.summary,
        details: normalizeDetailsForSave(p.details),
        design_team: p.design_team,
        gallery: p.gallery,
        before_after: (p.before_after || []).map((it, idx) => ({
          id: it.id,
          before_image: it.before_image || it.beforeImage || "",
          after_image: it.after_image || it.afterImage || "",
          title: it.title || "",
          caption: it.caption || "",
          sort_order: Number.isFinite(Number(it.sort_order))
            ? Number(it.sort_order)
            : idx,
        })),
        videos: p.videos || [],
        status: p.status,
        featured: p.featured,
        sort_order: p.sort_order,
        seo: p.seo,
      };

      const updated = await adminApi.updateProject(p.slug, patch);
      setP(updated);
      toast.success("Project saved.");
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (p === null) {
    return (
      <EmptyState
        title="Project not found"
        description="The project may have been deleted."
        action={
          <Link to="/admin/projects" className="btn-outline">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        }
      />
    );
  }

  if (!p) {
    return (
      <div className="h-8 w-40 bg-ink-graphite animate-pulse rounded-sm" />
    );
  }

  const detailExtraRows = getDetailExtraRows(p.details?.extra);

  const updateDetailExtraRows = (rows) => {
    setP({
      ...p,
      details: {
        ...(p.details || {}),
        extra: rows,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/projects" className="btn-ghost link-underline">
            <ArrowLeft className="h-3.5 w-3.5" />
            All projects
          </Link>

          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">
            {p.title}
          </h2>

          <p className="text-xs text-brand-gray mt-1">
            /{p.slug}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={`/residential-design/${p.slug}`}
            target="_blank"
            rel="noreferrer"
            className="btn-outline"
          >
            Preview
          </Link>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
            <Save className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/8">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ${
              tab === t
                ? "text-brand-yellow border-b-2 border-brand-yellow"
                : "text-brand-gray hover:text-brand-white"
            }`}
          >
            {t.replaceAll("_", " ")}
          </button>
        ))}
      </div>

      <div className="admin-card rounded-sm p-5 md:p-7">
        {tab === "basic" && (
          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <FieldLabel>Title</FieldLabel>
              <AdminInput
                value={p.title || ""}
                onChange={(e) => setP({ ...p, title: e.target.value })}
              />
            </label>

            <label>
              <FieldLabel>Short label</FieldLabel>
              <AdminInput
                value={p.short_label || ""}
                onChange={(e) => setP({ ...p, short_label: e.target.value })}
              />
            </label>

            <div className="md:col-span-2">
              <FieldLabel>Cover image</FieldLabel>
              <ImageUploadField
                value={p.cover_image || ""}
                onChange={(value) => setP({ ...p, cover_image: value })}
                category="residential"
              />
            </div>

            <label>
              <FieldLabel>Status</FieldLabel>
              <AdminSelect
                value={p.status}
                onChange={(e) => setP({ ...p, status: e.target.value })}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </AdminSelect>
            </label>

            <label>
              <FieldLabel>Featured</FieldLabel>
              <AdminSelect
                value={String(p.featured)}
                onChange={(e) =>
                  setP({ ...p, featured: e.target.value === "true" })
                }
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </AdminSelect>
            </label>

            <label>
              <FieldLabel>Sort order</FieldLabel>
              <AdminInput
                type="number"
                value={p.sort_order ?? 0}
                onChange={(e) =>
                  setP({ ...p, sort_order: Number(e.target.value) })
                }
              />
            </label>
          </div>
        )}

        {tab === "motivation" && (
          <label className="block">
            <FieldLabel>Motivation</FieldLabel>
            <AdminTextarea
              rows={12}
              value={p.motivation || ""}
              onChange={(e) => setP({ ...p, motivation: e.target.value })}
            />
          </label>
        )}

        {tab === "summary" && (
          <label className="block">
            <FieldLabel>Summary</FieldLabel>
            <AdminTextarea
              rows={16}
              value={p.summary || ""}
              onChange={(e) => setP({ ...p, summary: e.target.value })}
            />
          </label>
        )}

        {tab === "details" && (
          <div className="space-y-8">
            <div>
              <p className="eyebrow-muted mb-4">
                Fixed Details
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <label>
                  <FieldLabel>Location</FieldLabel>
                  <AdminInput
                    value={p.details?.location || ""}
                    onChange={(e) =>
                      setP({
                        ...p,
                        details: {
                          ...(p.details || {}),
                          location: e.target.value,
                        },
                      })
                    }
                  />
                </label>

                <label>
                  <FieldLabel>Type</FieldLabel>
                  <AdminInput
                    value={p.details?.type || ""}
                    onChange={(e) =>
                      setP({
                        ...p,
                        details: {
                          ...(p.details || {}),
                          type: e.target.value,
                        },
                      })
                    }
                  />
                </label>

                <label>
                  <FieldLabel>Area</FieldLabel>
                  <AdminInput
                    value={p.details?.area || ""}
                    onChange={(e) =>
                      setP({
                        ...p,
                        details: {
                          ...(p.details || {}),
                          area: e.target.value,
                        },
                      })
                    }
                  />
                </label>

                <label>
                  <FieldLabel>Year Built</FieldLabel>
                  <AdminInput
                    value={p.details?.year_built || ""}
                    onChange={(e) =>
                      setP({
                        ...p,
                        details: {
                          ...(p.details || {}),
                          year_built: e.target.value,
                        },
                      })
                    }
                  />
                </label>
              </div>
            </div>

            <div className="space-y-4 border border-white/8 rounded-sm p-4 md:p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <FieldLabel>Additional Details</FieldLabel>
                  <p className="text-[11px] text-brand-gray mt-1 max-w-2xl">
                    Add any extra project details you need. These fields will appear in the public Details tab after the fixed fields.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn-outline text-[10px] px-3 py-2"
                  onClick={() =>
                    updateDetailExtraRows([
                      ...detailExtraRows,
                      { label: "", value: "" },
                    ])
                  }
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add field
                </button>
              </div>

              {detailExtraRows.length === 0 ? (
                <div className="admin-card rounded-sm p-5 text-center">
                  <p className="body-text text-sm">
                    No additional details yet.
                  </p>
                </div>
              ) : null}

              {detailExtraRows.map((row, index) => (
                <div
                  key={`extra-detail-${index}`}
                  className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
                >
                  <label>
                    <FieldLabel>Field label {index + 1}</FieldLabel>
                    <AdminInput
                      value={row.label || ""}
                      placeholder="e.g. Bedrooms"
                      onChange={(e) => {
                        const rows = [...detailExtraRows];
                        rows[index] = {
                          ...rows[index],
                          label: e.target.value,
                        };
                        updateDetailExtraRows(rows);
                      }}
                    />
                  </label>

                  <label>
                    <FieldLabel>Value</FieldLabel>
                    <AdminInput
                      value={row.value || ""}
                      placeholder="e.g. 4"
                      onChange={(e) => {
                        const rows = [...detailExtraRows];
                        rows[index] = {
                          ...rows[index],
                          value: e.target.value,
                        };
                        updateDetailExtraRows(rows);
                      }}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      updateDetailExtraRows(
                        detailExtraRows.filter((_, i) => i !== index)
                      )
                    }
                    className="h-10 w-10 grid place-items-center border border-white/10 rounded-sm text-brand-red"
                    aria-label={`Remove detail ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "design_team" && (
          <div className="space-y-3">
            {(p.design_team || []).map((m, i) => (
              <div
                key={i}
                className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
              >
                <label>
                  <FieldLabel>Role</FieldLabel>
                  <AdminInput
                    value={m.role}
                    onChange={(e) => {
                      const arr = [...(p.design_team || [])];
                      arr[i] = { ...arr[i], role: e.target.value };
                      setP({ ...p, design_team: arr });
                    }}
                  />
                </label>

                <label>
                  <FieldLabel>Name</FieldLabel>
                  <AdminInput
                    value={m.name}
                    onChange={(e) => {
                      const arr = [...(p.design_team || [])];
                      arr[i] = { ...arr[i], name: e.target.value };
                      setP({ ...p, design_team: arr });
                    }}
                  />
                </label>

                <button
                  type="button"
                  onClick={() => {
                    const arr = (p.design_team || []).filter(
                      (_, j) => j !== i
                    );
                    setP({ ...p, design_team: arr });
                  }}
                  className="h-10 w-10 grid place-items-center border border-white/10 rounded-sm text-brand-red"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() =>
                setP({
                  ...p,
                  design_team: [
                    ...(p.design_team || []),
                    { role: "", name: "" },
                  ],
                })
              }
              className="btn-outline"
            >
              Add member
            </button>
          </div>
        )}

        {tab === "before_after" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="body-text text-sm max-w-2xl">
                  Add one or more Before / After comparisons. Each item has a before image, an after image, an optional title and caption. Items are shown in order on the public project page.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setP({
                    ...p,
                    before_after: [
                      ...(p.before_after || []),
                      {
                        before_image: "",
                        after_image: "",
                        title: "",
                        caption: "",
                        sort_order: (p.before_after || []).length,
                      },
                    ],
                  })
                }
                className="btn-primary"
              >
                <ImagePlus className="h-4 w-4" />
                Add comparison
              </button>
            </div>

            {(p.before_after || []).length === 0 ? (
              <div className="admin-card rounded-sm p-6 text-center">
                <p className="body-text text-sm">
                  No before/after comparisons yet. Add one to reveal a draggable slider on the project page.
                </p>
              </div>
            ) : null}

            {(p.before_after || []).map((it, i) => {
              const move = (direction) => {
                const arr = [...(p.before_after || [])];
                const target = i + direction;

                if (target < 0 || target >= arr.length) return;

                const tmp = arr[i];
                arr[i] = arr[target];
                arr[target] = tmp;
                arr.forEach((row, idx) => {
                  row.sort_order = idx;
                });

                setP({ ...p, before_after: arr });
              };

              const remove = () => {
                if (
                  typeof window !== "undefined" &&
                  !window.confirm("Delete this before/after comparison?")
                ) {
                  return;
                }

                const arr = (p.before_after || [])
                  .filter((_, j) => j !== i)
                  .map((row, idx) => ({ ...row, sort_order: idx }));

                setP({ ...p, before_after: arr });
              };

              const patchItem = (patch) => {
                const arr = [...(p.before_after || [])];
                arr[i] = { ...arr[i], ...patch };
                setP({ ...p, before_after: arr });
              };

              return (
                <div
                  key={it.id || i}
                  className="admin-card rounded-sm p-5 md:p-6 space-y-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="eyebrow-muted">
                      Comparison {String(i + 1).padStart(2, "0")}
                    </p>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => move(-1)}
                        disabled={i === 0}
                        className="h-9 w-9 grid place-items-center border border-white/10 rounded-sm text-brand-white/85 disabled:opacity-40"
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => move(1)}
                        disabled={i === (p.before_after || []).length - 1}
                        className="h-9 w-9 grid place-items-center border border-white/10 rounded-sm text-brand-white/85 disabled:opacity-40"
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={remove}
                        className="h-9 px-3 grid place-items-center border border-white/10 rounded-sm text-brand-red hover:border-brand-red"
                        aria-label="Delete comparison"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Before image</FieldLabel>
                      <ImageUploadField
                        value={it.before_image || it.beforeImage || ""}
                        category="residential"
                        onChange={(value) => patchItem({ before_image: value })}
                      />
                    </div>

                    <div>
                      <FieldLabel>After image</FieldLabel>
                      <ImageUploadField
                        value={it.after_image || it.afterImage || ""}
                        category="residential"
                        onChange={(value) => patchItem({ after_image: value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <label>
                      <FieldLabel>Title (optional)</FieldLabel>
                      <AdminInput
                        value={it.title || ""}
                        placeholder="e.g. Living Room"
                        onChange={(e) => patchItem({ title: e.target.value })}
                      />
                    </label>

                    <label>
                      <FieldLabel>Caption (optional)</FieldLabel>
                      <AdminInput
                        value={it.caption || ""}
                        placeholder="Short description"
                        onChange={(e) => patchItem({ caption: e.target.value })}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "gallery" && (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(p.gallery || []).map((g, i) => (
                <div
                  key={g.id || i}
                  className="admin-card rounded-sm overflow-hidden"
                >
                  <div className="aspect-[4/3] bg-ink-graphite">
                    {g.url ? (
                      <img
                        src={g.url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>

                  <div className="p-3 space-y-2">
                    <ImageUploadField
                      value={g.url || ""}
                      placeholder="Image URL or upload"
                      category="residential"
                      onChange={(value) => {
                        const arr = [...(p.gallery || [])];
                        arr[i] = { ...arr[i], url: value };
                        setP({ ...p, gallery: arr });
                      }}
                    />

                    <AdminInput
                      value={g.alt || ""}
                      placeholder="Alt text"
                      onChange={(e) => {
                        const arr = [...(p.gallery || [])];
                        arr[i] = { ...arr[i], alt: e.target.value };
                        setP({ ...p, gallery: arr });
                      }}
                    />

                    <AdminInput
                      value={g.caption || ""}
                      placeholder="Caption"
                      onChange={(e) => {
                        const arr = [...(p.gallery || [])];
                        arr[i] = { ...arr[i], caption: e.target.value };
                        setP({ ...p, gallery: arr });
                      }}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setP({
                          ...p,
                          gallery: (p.gallery || []).filter((_, j) => j !== i),
                        })
                      }
                      className="btn-ghost text-brand-red"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() =>
                setP({
                  ...p,
                  gallery: [
                    ...(p.gallery || []),
                    {
                      url: "",
                      alt: "",
                      sort_order: (p.gallery || []).length,
                    },
                  ],
                })
              }
              className="btn-outline"
            >
              <ImagePlus className="h-4 w-4" />
              Add gallery image
            </button>
          </div>
        )}

        {tab === "videos" && (
          <div className="space-y-4">
            <p className="body-text text-sm">
              Add or edit video URLs for this residential project. Casa PC videos from the provided material are loaded here by default.
            </p>

            {(p.videos || []).map((video, i) => (
              <div
                key={`${video}-${i}`}
                className="grid md:grid-cols-[1fr_auto] gap-3 items-end"
              >
                <label>
                  <FieldLabel>Video URL {i + 1}</FieldLabel>
                  <AdminInput
                    value={video || ""}
                    onChange={(e) => {
                      const arr = [...(p.videos || [])];
                      arr[i] = e.target.value;
                      setP({ ...p, videos: arr });
                    }}
                  />
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setP({
                      ...p,
                      videos: (p.videos || []).filter((_, j) => j !== i),
                    })
                  }
                  className="h-10 w-10 grid place-items-center border border-white/10 rounded-sm text-brand-red"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setP({ ...p, videos: [...(p.videos || []), ""] })}
              className="btn-outline"
            >
              Add video
            </button>
          </div>
        )}

        {tab === "seo" && (
          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <FieldLabel>SEO Title</FieldLabel>
              <AdminInput
                value={p.seo?.title || ""}
                onChange={(e) =>
                  setP({
                    ...p,
                    seo: {
                      ...(p.seo || {}),
                      title: e.target.value,
                    },
                  })
                }
              />
            </label>

            <label>
              <FieldLabel>Canonical URL</FieldLabel>
              <AdminInput
                value={p.seo?.canonical || ""}
                onChange={(e) =>
                  setP({
                    ...p,
                    seo: {
                      ...(p.seo || {}),
                      canonical: e.target.value,
                    },
                  })
                }
              />
            </label>

            <label className="md:col-span-2">
              <FieldLabel>SEO Description</FieldLabel>
              <AdminTextarea
                rows={4}
                value={p.seo?.description || ""}
                onChange={(e) =>
                  setP({
                    ...p,
                    seo: {
                      ...(p.seo || {}),
                      description: e.target.value,
                    },
                  })
                }
              />
            </label>

            <div>
              <FieldLabel>OG Image</FieldLabel>
              <ImageUploadField
                value={p.seo?.og_image || ""}
                onChange={(value) =>
                  setP({
                    ...p,
                    seo: {
                      ...(p.seo || {}),
                      og_image: value,
                    },
                  })
                }
                category="general"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}